import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SupporterSortField } from '@/api/generated';
import type { SortDirection } from '@/components/ui/DataTable';

/**
 * The Supporters table's view, kept in the page's address (P5, P14): which
 * list, what search, which sort direction, which page and how many rows a
 * page holds. A reload and the back button return to the same view. Defaults
 * are left out of the address, and a value outside its vocabulary falls back
 * to the default rather than reaching the backend's 422.
 */

export const TYPES = ['followed', 'invited'] as const;
export type SupporterListType = (typeof TYPES)[number];

export const DIRECTIONS: readonly SortDirection[] = ['asc', 'desc'];

/** Supporter Since, the one column the lists sort by (backend D13). */
export const SORT_COLUMN: SupporterSortField = 'joined_at';

/** The rows-per-page choices (P14); the backend accepts 1 to 100 (D13). */
export const PAGE_SIZES = [20, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

export interface SupportersParams {
  /** Followed reads the supporters route, invited the members route (P1). */
  type: SupporterListType;
  search: string;
  /** Of Supporter Since, the one sortable column. */
  direction: SortDirection;
  page: number;
  limit: PageSize;
}

export const DEFAULTS: SupportersParams = {
  type: 'followed',
  search: '',
  direction: 'desc',
  page: 1,
  limit: 20,
};

function oneOf<T extends string | number>(
  value: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  if (value === null) return fallback;
  const match = allowed.find((candidate) => String(candidate) === value);
  return match ?? fallback;
}

export function parseParams(search: URLSearchParams): SupportersParams {
  const page = Number(search.get('page'));
  return {
    type: oneOf(search.get('type'), TYPES, DEFAULTS.type),
    search: search.get('search') ?? DEFAULTS.search,
    direction: oneOf(search.get('direction'), DIRECTIONS, DEFAULTS.direction),
    page: Number.isInteger(page) && page >= 1 ? page : DEFAULTS.page,
    limit: oneOf(search.get('limit'), PAGE_SIZES, DEFAULTS.limit),
  };
}

export function serializeParams(params: SupportersParams): URLSearchParams {
  const search = new URLSearchParams();
  for (const key of Object.keys(DEFAULTS) as (keyof SupportersParams)[]) {
    if (params[key] !== DEFAULTS[key]) search.set(key, String(params[key]));
  }
  return search;
}

/**
 * A new view from a change. Anything but turning the page returns to the
 * first page, the page size included, because the old page number belongs
 * to the old list (I4).
 */
export function withChange(
  params: SupportersParams,
  patch: Partial<SupportersParams>,
): SupportersParams {
  const pageOnly = Object.keys(patch).every((key) => key === 'page');
  return { ...params, ...(pageOnly ? {} : { page: 1 }), ...patch };
}

export function useSupportersParams(): [
  SupportersParams,
  (patch: Partial<SupportersParams>) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parseParams(searchParams), [searchParams]);
  const change = useCallback(
    (patch: Partial<SupportersParams>) => {
      setSearchParams(serializeParams(withChange(parseParams(searchParams), patch)), {
        replace: true,
      });
    },
    [searchParams, setSearchParams],
  );
  return [params, change];
}
